import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { Controller, useFieldArray } from 'react-hook-form';

import {
  ArrowNarrowLeftIcon,
  BookOpenIcon,
  ChevronDownIcon,
  SparklesIcon,
  ClipboardCheckIcon,
} from '@heroicons/react/outline';

import { Combobox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { parseMarkdown } from '../../lib/markdown';
import { $convertToMarkdownString } from '@lexical/markdown';
import { MARKDOWN_TRANSFORMERS } from '../../components/Editor/plugins/MarkdownTransformers';

import {
  ActivityPdf,
  ActivityType,
  ActivityUseType,
  Feature,
  FollowUpTemplateDataFragment,
  PatientDataPermission,
  PatientLifecycleStatusV2,
  ProgramTagDataFragment,
  MailSequenceReferenceDateType,
  PatientNameDataFragment,
  useAddVoiceNoteMutation,
  useEditActivityFileSettingsMutation,
  useMeProviderProgramTagsWithCountQuery,
  usePatientsV2NamesQuery,
  useProgramNotesQuery,
  usePatientQuery,
} from '../../../generated/graphql';

import Spinner from '../../svgs/Spinner';

import { useAuth } from '../../../contexts/AuthContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagContext';
import { isProviderUserOwnerOrAdminAuthorized } from '../../../lib/auth';

import { getTimeStamp } from '../../lib/time';

import useFixPage from '../../hooks/useFixPage';
import usePaintScreen from '../../hooks/usePaintScreen';

import Button from '../../components/Button';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import ResourceInUseModal from '../../components/Modals/ResourceInUseModal';
import {
  parseAutomationReferencesFromError,
  type AutomationReference,
} from '../../lib/automationHelpers';
import Editor from '../../components/Editor';
import '../../components/Editor/index.css';
import IconButton from '../../components/IconButton';
import FollowUpIntroModal from '../../components/Modals/FollowUpIntroModal';
import { sortPatientsByAlphabeticalAscending } from '../../components/PatientItem';

import { isAbortError } from '../../../lib/errors';
import { logger } from '../../../lib/logger';
import AudioRecorderPlayer, {
  AudioRecorderPlayerRef,
} from '../../components/AudioRecorderPlayer';
import Avatar from '../../components/Avatar';
import AddAudioModal from '../../components/Modals/AddAudioModal';
import AddImageModal from '../../components/Modals/AddImageModal';
import AddLinkModal from '../../components/Modals/AddLinkModal';
import AddOrEditTextModal from '../../components/Modals/AddOrEditTextModal';
import AddResourceModal, {
  AddResourceModalRef,
} from '../../components/Modals/AddResourceModal';
import AddStandardAssessmentsModal from '../../components/Modals/AddStandardAssessmentsModal';
import SectionLabel from '../../components/SectionLabel';
import PillButton from '../../components/PillButton';
import {
  SavedActivityInputPreview,
  defaultActivitySchema,
} from '../../lib/followUp';
import ActivityCreationCard from './ActivityCreationCard';
import FollowUpTitle from './FollowUpTitle';
import { MAX_SUBJECT_LENGTH, convertPatientToPatientNameData } from './helpers';
import Tag from '../../svgs/Tag';
import { pluralize } from '../../lib/copy';
import { formatProviderActivityForFollowUp } from '../../lib/providerActivity';
import CircleCheck from '../../svgs/CircleCheck';
import AddPDFModal from '../../components/Modals/AddPDFModal';
import FileSettingsModal from '../../components/Modals/FileSettingsModal';
import { FILE_ACTIVITY_TYPES } from '../../lib/activity';
import SendToTagConfirmationModal from './SendToTagConfirmationModal';
import ProgramTagPill from './ProgramTagPill';
import ReviewSignerDetailsModal from '../../components/Modals/MultiSignatureModals/ReviewSignerDetailsModal';
import { customToast } from '../../components/ToastAlert/customToast';
import PageContainer from '../../components/Containers/PageContainer';
import FollowUpDrawer from './FollowUpDrawer';
import SaveTemplateButton from './SaveTemplateButton';
import NameFollowUpTemplateModal from './NameFollowUpTemplateModal';
import InputGroup from '../../components/InputGroup';
import PatientPill from './PatientPill';
import AddANewResourceMenu from '../../components/AddANewResourceMenu';
import {
  ScheduleMailLocationState,
  SCHEDULE_MAIL_PATH,
} from '../ScheduleMail/types';
import {
  getEnrollableRecipientPatients,
  useRecipientSelection,
} from './hooks/useRecipientSelection';
import { useFollowUpForm } from './hooks/useFollowUpForm';
import { useFollowUpSubmission } from './hooks/useFollowUpSubmission';

const FollowUpBuilder = () => {
  useFixPage();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    initialActivities?: SavedActivityInputPreview[];
    initialProgramTags?: ProgramTagDataFragment[];
    applyFollowUpTemplate?: FollowUpTemplateDataFragment;
  } | null;
  const showPage = usePaintScreen();
  const { authedProviderUser, showUpgradeBanner } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const isMailSequencesEnabled = isFeatureEnabled(Feature.MailSequences);

  const programId = params.programId ?? '';
  const followUpTemplateId = params.followUpTemplateId ?? null;
  const initialActivities = locationState?.initialActivities;
  const initialProgramTags = locationState?.initialProgramTags;
  const applyFollowUpTemplateFromNav = locationState?.applyFollowUpTemplate;
  const appliedTemplateFromNavRef = useRef(false);
  const templateApplyAttemptsRef = useRef(0);
  const [templateRetryNonce, setTemplateRetryNonce] = useState(0);

  const [editorKey, setEditorKey] = useState(0);

  const [isAddPDFModalOpen, setIsAddPDFModalOpen] = useState(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [isAddAudioModalOpen, setIsAddAudioModalOpen] = useState(false);
  const [isAddImageModalOpen, setIsAddImageModalOpen] = useState(false);
  const [isAddOrEditTextModalOpen, setIsAddOrEditTextModalOpen] =
    useState(false);

  const addResourceModalRef = useRef<AddResourceModalRef | null>(null);
  const [isAddResourceModalOpen, setIsAddResourceModalOpen] = useState(false);
  const [
    isAddStandardAssessmentsModalOpen,
    setIsAddStandardAssessmentsModalOpen,
  ] = useState(false);
  const [isFileSettingsModalOpen, setIsFileSettingsModalOpen] = useState(false);

  const [isFollowUpIntroModalOpen, setIsFollowUpIntroModalOpen] =
    useState(false);
  const [isNameFollowUpTemplateModalOpen, setIsNameFollowUpTemplateModalOpen] =
    useState(false);
  const [
    isRenameFollowUpTemplateModalOpen,
    setIsRenameFollowUpTemplateModalOpen,
  ] = useState(false);

  const audioRecorderPlayerRef = useRef<AudioRecorderPlayerRef | null>(null);
  const [draftedVoiceNoteMediaUrl, setDraftedVoiceNoteMediaUrl] = useState<
    string | undefined
  >();

  // This used to be mutable, but we walked that back, so this is permanently false
  const isIntake = false;

  const abortAddVoiceNote = useRef(new AbortController());

  const hasSentFollowUps = Boolean(authedProviderUser?.hasSentFollowUps);

  // --- Hooks Integration ---

  const {
    form,
    isSavingDraft,
    currentDraftLastSavedAt,
    currentFollowUpTemplateDraftId,
    followUpTemplates,
    followUpDrafts,
    handleSaveFollowUpTemplate,
    handleApplyFollowUpTemplate,
    handleRenameFollowUpTemplate,
    handleDuplicateFollowUpTemplate,
    handleDeleteFollowUpTemplate,
    saveFollowUpTemplateMutationLoading,
  } = useFollowUpForm({
    programId,
    followUpTemplateId,
    initialActivities,
    initialProgramTags,
    audioRecorderPlayerRef,
    setDraftedVoiceNoteMediaUrl,
    setEditorKey,
  });

  const {
    control,
    watch,
    register,
    setValue,
    formState: { errors },
  } = form;

  const watchPatientsOrTags = watch('patientsOrTags');
  const watchSubject = watch('subject');
  const onlyPatientOrTag =
    watchPatientsOrTags.length === 1 && watchPatientsOrTags?.[0];
  const onlyPatient =
    onlyPatientOrTag?.__typename === 'PatientName' && onlyPatientOrTag;

  const { data: meProviderProgramTagsData } =
    useMeProviderProgramTagsWithCountQuery();

  const providerProgramTags =
    meProviderProgramTagsData?.meProvider?.provider?.programTags ?? [];

  const { data: patientsData } = usePatientsV2NamesQuery({
    variables: {
      patientLifecycleStatuses: [
        PatientLifecycleStatusV2.Invited,
        PatientLifecycleStatusV2.Active,
      ],
      assignedPatients:
        authedProviderUser.patientDataPermission ===
        PatientDataPermission.Assigned,
    },
  });

  const patientsList = sortPatientsByAlphabeticalAscending(
    patientsData?.patientNames?.filter((patient) =>
      // If doesn't have premium access, can't send follow-ups to existing patients, but can send to test patients
      // so filter out real patients here.
      authedProviderUser?.hasPremiumAccess ? true : patient.isTestProgram,
    ),
  );

  const { data: patientData, refetch: refetchPatient } = usePatientQuery({
    variables: {
      programId: onlyPatient?.programInstanceId || programId,
    },
    skip: !onlyPatient?.programInstanceId && !programId,
    onError: () => {
      customToast.error('Unable to fetch client.');
    },
  });

  useEffect(() => {
    if (patientData?.patient && !watchPatientsOrTags.length) {
      const patientNameData = convertPatientToPatientNameData(
        patientData.patient,
      );
      setValue('patientsOrTags', [patientNameData]);
    }
  }, [patientData]);

  useEffect(() => {
    if (appliedTemplateFromNavRef.current || !applyFollowUpTemplateFromNav) {
      return;
    }
    appliedTemplateFromNavRef.current = true;
    void handleApplyFollowUpTemplate(applyFollowUpTemplateFromNav)
      .then(() => {
        navigate(location.pathname, { replace: true, state: null });
      })
      .catch((error) => {
        // Retry once by bumping state (an effect dependency, so the effect is
        // guaranteed to re-run); capped so a persistently failing template
        // can't loop.
        templateApplyAttemptsRef.current += 1;
        if (templateApplyAttemptsRef.current < 2) {
          appliedTemplateFromNavRef.current = false;
          setTemplateRetryNonce((n) => n + 1);
        } else {
          customToast.error('Unable to apply the follow-up template.');
        }
        logger.error('Failed to apply follow-up template from navigation', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
  }, [
    applyFollowUpTemplateFromNav,
    handleApplyFollowUpTemplate,
    location.pathname,
    navigate,
    templateRetryNonce,
  ]);

  const {
    tagSelections,
    setTagSelections,
    tagOrder,
    setTagOrder,
    patientSelections,
    setPatientSelections,
    patientOrder,
    setPatientOrder,
    setPatientOrTagQuery,
    filteredPatientsOrTags,
    cloneSelectionState,
    removePatientFromState,
    removeTagFromState,
    buildRecipientsValue,
    setRecipients,
  } = useRecipientSelection({
    watchPatientsOrTags,
    providerProgramTags,
    patientsList: patientsList as PatientNameDataFragment[],
  });

  const {
    submitting,
    sendingFollowUp,
    sendFollowUpWithCheck,
    isSendToTagConfirmationModalOpen,
    setIsSendToTagConfirmationModalOpen,
    recipientPatientNames,
    isReviewSignerDetailsModalOpen,
    setIsReviewSignerDetailsModalOpen,
    unassignedMultiSignatureActivities,
    patientsDataForModal,
    refetchPatientForModal,
  } = useFollowUpSubmission({
    form,
    currentFollowUpTemplateDraftId,
    followUpDrafts,
    patientSelections,
    tagOrder,
    isIntake,
    patientsData,
  });

  const [addVoiceNoteMutation, { loading: addVoiceNoteLoading }] =
    useAddVoiceNoteMutation();

  const todayFormatted = getTimeStamp(
    new Date().toISOString(),
    false,
    false,
    false,
    true,
  );

  const hasMultipleRecipients =
    watchPatientsOrTags.length > 1 ||
    watchPatientsOrTags?.[0]?.__typename === 'ProgramTag';

  const enrollableRecipientPatients = useMemo(
    () => getEnrollableRecipientPatients(patientSelections),
    [patientSelections],
  );

  const enrollableProgramIds = useMemo(
    () =>
      Array.from(
        new Set(enrollableRecipientPatients.map((p) => p.programInstanceId)),
      ),
    [enrollableRecipientPatients],
  );

  const isPatientName = (item: any): item is PatientNameDataFragment =>
    item?.__typename === 'PatientName';

  const isProgramTag = (item: any): item is ProgramTagDataFragment =>
    item?.__typename === 'ProgramTag';

  const watchNote = watch('note');
  const watchProviderVoiceNoteId = watch('providerVoiceNoteId');
  const [selectedActivityPdfId, setSelectedActivityPdfId] = useState<string>();

  const {
    fields: activities,
    remove,
    prepend,
    update,
  } = useFieldArray({
    control,
    name: 'activities',
  });

  const hasRecordedVoiceNote = watchProviderVoiceNoteId !== null;
  const hasMessage = watchNote.trim().length || hasRecordedVoiceNote;
  const hasActivities = Boolean(activities.length);

  const handleScheduleClick = () => {
    const today = new Date();
    const customAnchorDate = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0'),
    ].join('-');

    const scheduleState: ScheduleMailLocationState = {
      programIds: enrollableProgramIds,
      recipientPatients: enrollableRecipientPatients,
      activities: activities as SavedActivityInputPreview[],
      subject: watchSubject,
      providerNote: watchNote,
      providerVoiceNoteId: watchProviderVoiceNoteId,
      isIntake,
      referenceDateType: MailSequenceReferenceDateType.Custom,
      customAnchorDate,
      returnTo: `${location.pathname}${location.search}`,
    };
    navigate(SCHEDULE_MAIL_PATH, { state: scheduleState });
  };

  const hasValidForm =
    watchPatientsOrTags.length &&
    (hasMessage || hasActivities) &&
    !addVoiceNoteLoading;

  const canSaveTemplate =
    (hasMessage || hasActivities) && !addVoiceNoteLoading && !submitting;

  const { data: programNotesData } = useProgramNotesQuery({
    variables: {
      programId: onlyPatient?.programInstanceId,
    },
    skip: !onlyPatient?.programInstanceId || watchPatientsOrTags?.length > 1,
    onError: (error) => {
      customToast.error('Unable to fetch notes.');
    },
  });

  // Convert markdown notes to HTML
  const programNotes = useMemo(
    () =>
      programNotesData?.programNotes
        ? programNotesData?.programNotes.map((programNote) => ({
            ...programNote,
            note: parseMarkdown(programNote.note),
          }))
        : [],
    [programNotesData?.programNotes],
  );

  const [followUpTemplateToRename, setFollowUpTemplateToRename] = useState<
    FollowUpTemplateDataFragment | undefined
  >();

  const [templateInUseInfo, setTemplateInUseInfo] = useState<{
    templateName: string;
    automationReferences: AutomationReference[];
  } | null>(null);

  const handleRenameFollowUpTemplateClick = async (
    followUpTemplate: FollowUpTemplateDataFragment,
  ) => {
    setFollowUpTemplateToRename(followUpTemplate);
    setIsRenameFollowUpTemplateModalOpen(true);
  };

  const handleDeleteFollowUpTemplateWithModal = async (
    followUpTemplate: FollowUpTemplateDataFragment,
  ) => {
    try {
      await handleDeleteFollowUpTemplate(followUpTemplate);
    } catch (error) {
      // Check if this is a RESOURCE_IN_USE error
      if (error instanceof ApolloError && error.graphQLErrors?.[0]) {
        const graphQLError = error.graphQLErrors[0];
        const errorMessage = graphQLError.message || '';

        const automationReferences =
          parseAutomationReferencesFromError(errorMessage);
        if (automationReferences) {
          setTemplateInUseInfo({
            templateName: followUpTemplate.name,
            automationReferences,
          });
          return;
        }
      }

      // Handle other errors - show generic error
      customToast.error(
        `Failed to delete ${followUpTemplate.isDraft ? 'draft' : 'template'}.`,
      );
    }
  };

  const onAddVoiceNote = async (audioFile: File, audioDuration: number) => {
    try {
      const { data } = await addVoiceNoteMutation({
        variables: {
          input: {
            file: audioFile,
            duration: audioDuration,
          },
        },
        context: { fetchOptions: { signal: abortAddVoiceNote.current.signal } },
      });
      setValue('providerVoiceNoteId', data?.addVoiceNote.id ?? null);
    } catch (err) {
      if (isAbortError(err as any)) return;
      customToast.error('Something went wrong.');
    }
  };

  const onDiscardVoiceNote = async () => {
    abortAddVoiceNote.current && abortAddVoiceNote.current.abort();
    abortAddVoiceNote.current = new AbortController();
    setValue('providerVoiceNoteId', null);
    setDraftedVoiceNoteMediaUrl(undefined);
  };

  useEffect(() => {
    if (showPage && !hasSentFollowUps) setIsFollowUpIntroModalOpen(true);
  }, [hasSentFollowUps]);

  const [isDeleteFollowUpDraftModalOpen, setIsDeleteFollowUpDraftModalOpen] =
    useState(false);

  const addActionActivity = () => {
    const newActionActivity = {
      ...defaultActivitySchema,
      sendFollowUpActivityInputRaw: {
        ...defaultActivitySchema.sendFollowUpActivityInputRaw,
        dateCreated: Date.now(),
      },
    };

    prepend(newActionActivity);
  };

  const [editActivityFileSettingsMutation] =
    useEditActivityFileSettingsMutation();

  const handleUpdateActivityUseType = async (
    activityId: string,
    activityType: ActivityType,
    activityUseType: ActivityUseType,
    activityIndex: number,
  ) => {
    try {
      const editActivityFileSettingsInput = {
        activityId,
        activityType,
        activityUseType,
        ...(FILE_ACTIVITY_TYPES.includes(activityType) && {
          activityType: ActivityType.Pdf,
        }),
      };

      const { data } = await editActivityFileSettingsMutation({
        variables: { input: editActivityFileSettingsInput },
      });

      const updatedActivity = data?.editActivityFileSettings;

      if (updatedActivity) {
        customToast.success('Successfully changed file settings');
        update(
          activityIndex,
          formatProviderActivityForFollowUp(updatedActivity),
        );
      }
    } catch (err) {
      customToast.error((err as Error).message);
    }
  };

  // Helper function to check if a single activity requires the provider's signature first
  const requiresProviderSignatureFirst = (activity: any) =>
    activity?.sendFollowUpActivityInputRaw?.programAssessmentSignatureUsers?.some(
      ({ providerUserId }: any) => providerUserId === authedProviderUser?.id,
    ) &&
    activity?.multiSignatureAssessmentConfig?.assessmentSignatureUsers?.some(
      ({ signatureUserType, orderIndex }: any) =>
        signatureUserType === 'SendingProviderUser' && // Using string literal as enum might not be imported or available here easily without importing SignatureUser
        orderIndex === 0,
    );

  return (
    <>
      <PageContainer
        noPadding
        containerClassName={classNames(
          'relative',
          showUpgradeBanner
            ? 'h-[calc(100vh-var(--top-nav-height)-var(--upgrade-banner-height))]'
            : 'h-[calc(100vh-var(--top-nav-height))]',
        )}
      >
        <div className="fixed z-40 flex h-14 w-full flex-row items-center justify-between border-b border-neutral-50 bg-white py-3 px-20">
          <div className="flex min-w-[400px] flex-row items-center justify-start">
            {window.history.length > 1 && (
              <IconButton
                IconComponent={ArrowNarrowLeftIcon}
                iconClassName="text-secondary-100 w-5"
                aria-label="Back"
                className="rounded-xl px-3 py-2 ring-0"
                onClick={() => navigate(-1)}
              >
                <div className="small-caption ml-2 font-bold text-secondary-100">
                  Back
                </div>
              </IconButton>
            )}
          </div>
          <FollowUpTitle
            isSaving={isSavingDraft}
            lastSavedAt={currentDraftLastSavedAt}
          />
          <div className="flex min-w-[333px] flex-row items-center justify-end">
            {Boolean(sendingFollowUp) && (
              <Spinner className="mr-3 h-5 w-5 text-neutral-125" />
            )}
            <SaveTemplateButton
              disabled={!canSaveTemplate || saveFollowUpTemplateMutationLoading}
              existingTemplates={followUpTemplates}
              onClickSaveAsNewTemplate={async () =>
                setIsNameFollowUpTemplateModalOpen(true)
              }
              onClickUpdateExistingTemplate={async (
                followUpTemplate: FollowUpTemplateDataFragment,
              ) =>
                await handleSaveFollowUpTemplate(
                  followUpTemplate.name,
                  followUpTemplate.id,
                )
              }
            />
            {isMailSequencesEnabled && (
              <Button
                title="Schedule"
                theme="secondary"
                size="small"
                className="mr-3 py-1.5"
                onClick={handleScheduleClick}
                disabled={!hasValidForm || submitting || isSavingDraft}
              />
            )}
            <Button
              title="Send"
              size="small"
              className="px-6 py-1.5"
              onClick={() => sendFollowUpWithCheck()}
              disabled={!hasValidForm || submitting || isSavingDraft}
            />
          </div>
        </div>
        <div className="flex h-full flex-row pt-14">
          <FollowUpDrawer
            programNotes={programNotes}
            onlyPatient={Boolean(onlyPatient)}
            followUpTemplates={followUpTemplates}
            followUpDrafts={followUpDrafts}
            onApplyFollowUpTemplate={handleApplyFollowUpTemplate}
            onRenameFollowUpTemplate={handleRenameFollowUpTemplateClick}
            onDuplicateFollowUpTemplate={handleDuplicateFollowUpTemplate}
            onDeleteFollowUpTemplate={handleDeleteFollowUpTemplateWithModal}
          />
          <div className="flex h-auto flex-1 flex-col items-center overflow-y-auto pb-8">
            <div className="mt-16 w-9/12 max-w-[720px] pb-16">
              {/* Recipients section */}
              <SectionLabel title="Recipients" />
              <Controller
                name="patientsOrTags"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: {
                    value: true,
                    message: 'Choose your recipients or tags',
                  },
                }}
                render={({ field: { onChange } }) => {
                  const recipientsValue = buildRecipientsValue(
                    tagSelections,
                    tagOrder,
                    patientSelections,
                    patientOrder,
                  );

                  const applySelectionState = (
                    state: ReturnType<typeof cloneSelectionState>,
                    options: { resetQuery?: boolean } = {},
                  ) => {
                    setTagSelections(state.tagSelections);
                    setTagOrder(state.tagOrder);
                    setPatientSelections(state.patientSelections);
                    setPatientOrder(state.patientOrder);
                    const nextValue = buildRecipientsValue(
                      state.tagSelections,
                      state.tagOrder,
                      state.patientSelections,
                      state.patientOrder,
                    );
                    onChange(nextValue);
                    if (options.resetQuery) {
                      setPatientOrTagQuery('');
                    }
                  };

                  const handleRemovePatient = (patient: any) => {
                    const nextState = cloneSelectionState();
                    removePatientFromState(
                      nextState,
                      patient.programInstanceId,
                    );
                    applySelectionState(nextState);
                  };

                  const handleRemoveTag = (tag: any) => {
                    const nextState = cloneSelectionState();
                    removeTagFromState(nextState, tag.id);
                    applySelectionState(nextState);
                  };

                  const handleToggleTagPatientSelection = (
                    programTag: any,
                    patient: any,
                    nextSelected: boolean,
                  ) => {
                    const nextState = cloneSelectionState();
                    const tagEntry = nextState.tagSelections[programTag.id];
                    if (!tagEntry) {
                      return;
                    }

                    tagEntry.patients[patient.programInstanceId] = {
                      patient,
                      selected: nextSelected,
                    };

                    const patientEntry = nextState.patientSelections[
                      patient.programInstanceId
                    ] ?? {
                      patient,
                      manual: false,
                      selectedTagIds: [],
                    };

                    const selectedTagIdsSet = new Set(
                      patientEntry.selectedTagIds,
                    );

                    if (nextSelected) {
                      selectedTagIdsSet.add(programTag.id);
                    } else {
                      selectedTagIdsSet.delete(programTag.id);
                    }

                    patientEntry.selectedTagIds = Array.from(selectedTagIdsSet);

                    if (
                      !patientEntry.manual &&
                      patientEntry.selectedTagIds.length === 0
                    ) {
                      delete nextState.patientSelections[
                        patient.programInstanceId
                      ];
                      nextState.patientOrder = nextState.patientOrder.filter(
                        (id) => id !== patient.programInstanceId,
                      );
                    } else {
                      nextState.patientSelections[patient.programInstanceId] =
                        patientEntry;
                      if (
                        !nextState.patientOrder.includes(
                          patient.programInstanceId,
                        )
                      ) {
                        nextState.patientOrder.push(patient.programInstanceId);
                      }
                    }

                    const hasSelected = Object.values(tagEntry.patients).some(
                      (patientEntry) => patientEntry.selected,
                    );

                    if (!hasSelected) {
                      delete nextState.tagSelections[programTag.id];
                      nextState.tagOrder = nextState.tagOrder.filter(
                        (id) => id !== programTag.id,
                      );
                    } else {
                      nextState.tagSelections[programTag.id] = tagEntry;
                    }

                    applySelectionState(nextState);
                  };

                  const handleComboboxChange = async (nextSelection: any[]) => {
                    const nextValue = await setRecipients(nextSelection);
                    onChange(nextValue);
                    setPatientOrTagQuery('');
                  };

                  return (
                    <>
                      {Boolean(recipientsValue.length) && (
                        <>
                          <ul className="mb-4 flex flex-row flex-wrap gap-2">
                            {recipientsValue.map((recipient) => {
                              if (isPatientName(recipient)) {
                                return (
                                  <PatientPill
                                    key={recipient.programInstanceId}
                                    patient={recipient}
                                    onDelete={() =>
                                      handleRemovePatient(recipient)
                                    }
                                  />
                                );
                              }
                              if (isProgramTag(recipient)) {
                                const tagEntry = tagSelections[recipient.id];
                                const tagPatients = tagEntry
                                  ? Object.values(tagEntry.patients)
                                      .map(({ patient, selected }) => ({
                                        patient,
                                        selected,
                                      }))
                                      .sort((a, b) =>
                                        `${a.patient.firstName} ${a.patient.lastName}`
                                          .toLowerCase()
                                          .localeCompare(
                                            `${b.patient.firstName} ${b.patient.lastName}`.toLowerCase(),
                                          ),
                                      )
                                  : [];
                                return (
                                  <ProgramTagPill
                                    key={recipient.id}
                                    programTag={recipient}
                                    patients={tagPatients}
                                    onDelete={() => handleRemoveTag(recipient)}
                                    onTogglePatient={(
                                      programTag,
                                      patient,
                                      selected,
                                    ) =>
                                      handleToggleTagPatientSelection(
                                        programTag,
                                        patient,
                                        selected,
                                      )
                                    }
                                  />
                                );
                              }
                              return null;
                            })}
                          </ul>
                          {hasMultipleRecipients && (
                            <div className="mb-4 flex flex-row items-center gap-x-2 text-neutral-125">
                              <SparklesIcon className="h-6 min-w-[24px]" />
                              <div className="text-caption text-neutral-200">
                                Your follow up will be delivered to these
                                recipients separately.
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <Combobox
                        as="div"
                        className="relative mb-8 w-full text-body"
                        value={recipientsValue}
                        onChange={(selection) => {
                          void handleComboboxChange(selection);
                        }}
                        immediate
                        multiple
                      >
                        <Combobox.Input
                          className={classNames(
                            'relative flex w-full cursor-default flex-row items-center justify-start text-body placeholder:text-body',
                            'max-h-14 rounded-md bg-neutral-50 p-4 text-left text-secondary-150 shadow-sm sm:text-caption',
                            'border-0 placeholder:text-neutral-110 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50',
                            'selection:bg-secondary-20',
                          )}
                          onChange={(event) =>
                            setPatientOrTagQuery(event.target.value)
                          }
                          placeholder="Search recipients or tags"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronDownIcon
                            className="h-5 w-5 text-neutral-125"
                            aria-hidden="true"
                          />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setPatientOrTagQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75  bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                            {filteredPatientsOrTags.map((patientOrTag) => {
                              if (patientOrTag.__typename === 'PatientName') {
                                return (
                                  <Combobox.Option
                                    key={patientOrTag.programInstanceId}
                                    value={patientOrTag}
                                  >
                                    {({ active, selected }) => {
                                      const patientName = `${patientOrTag?.firstName} ${patientOrTag?.lastName}`;
                                      return (
                                        <div
                                          className={classNames(
                                            'relative flex cursor-pointer select-none flex-row items-center truncate py-3 px-5 text-green-150 hover:bg-neutral-25',
                                            {
                                              'bg-neutral-25':
                                                selected || active,
                                            },
                                          )}
                                        >
                                          <Avatar
                                            size="small"
                                            name={patientName}
                                            imageUrl={
                                              patientOrTag?.profileImageMedia
                                                ?.url
                                            }
                                            className="mr-3"
                                          />
                                          {patientName}
                                        </div>
                                      );
                                    }}
                                  </Combobox.Option>
                                );
                              } else if (
                                patientOrTag.__typename === 'ProgramTag'
                              ) {
                                const disabled = !Boolean(
                                  patientOrTag.programCount,
                                );
                                return (
                                  <Combobox.Option
                                    key={`tagOption_${patientOrTag.id}`}
                                    value={patientOrTag}
                                    disabled={disabled}
                                  >
                                    {({ active, selected }) => {
                                      return (
                                        <div
                                          className={classNames(
                                            'relative flex select-none flex-row truncate py-3 px-5 text-green-150 hover:bg-neutral-25',
                                            {
                                              'bg-neutral-25':
                                                selected || active,
                                            },
                                            disabled
                                              ? 'cursor-not-allowed opacity-50'
                                              : 'cursor-pointer',
                                          )}
                                        >
                                          <div className="flex w-full items-center space-x-3">
                                            <Tag className="h-6 min-w-[24px]" />
                                            <div className="flex-grow truncate text-caption">
                                              {patientOrTag.name}{' '}
                                              {`(${pluralize(
                                                patientOrTag.programCount,
                                                'client',
                                                'clients',
                                              )})`}
                                            </div>
                                            <div className="truncate text-small-caption text-neutral-100">
                                              Mail will deliver separately.
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }}
                                  </Combobox.Option>
                                );
                              }
                              return null;
                            })}
                          </Combobox.Options>
                        </Transition>
                      </Combobox>
                    </>
                  );
                }}
              />

              {/* Subject section */}
              <div className="mb-4">
                <SectionLabel title="Subject" />
                <InputGroup
                  inputSize="small"
                  placeholder="Subject"
                  label="Subject"
                  labelHidden
                  {...register('subject', {
                    maxLength: {
                      value: MAX_SUBJECT_LENGTH,
                      message: `Subject must be less than ${MAX_SUBJECT_LENGTH} characters`,
                    },
                  })}
                  errorMessage={errors.subject?.message}
                  maxLength={MAX_SUBJECT_LENGTH}
                />
              </div>

              {/* Message section */}
              <div
                className={classNames(
                  'mb-4 flex w-full',
                  !hasRecordedVoiceNote &&
                    'flex-row items-center justify-between',
                  hasRecordedVoiceNote && 'flex-col items-start justify-start',
                )}
              >
                <div className="flex flex-row items-center justify-center text-green-150">
                  <div className="text-body font-medium">Message</div>
                </div>
                <AudioRecorderPlayer
                  recordingEnabled={Boolean(watchPatientsOrTags.length)}
                  isUploading={addVoiceNoteLoading}
                  audioMediaUrl={draftedVoiceNoteMediaUrl}
                  hasUploaded={hasRecordedVoiceNote}
                  onAdd={onAddVoiceNote}
                  onDiscard={onDiscardVoiceNote}
                  filePrefix="voicenote"
                  controlsClassName={'!flex-row-reverse h-[36px]'}
                  ref={audioRecorderPlayerRef}
                />
              </div>
              <Controller
                control={control}
                name="note"
                rules={{ required: false }}
                render={({ field }) => {
                  const { onChange, value } = field;
                  return (
                    <Editor
                      elKey={`editor-${editorKey}`}
                      placeholder={`Hi ${
                        onlyPatient?.firstName ?? 'there'
                      }, to ${
                        isIntake
                          ? 'get started...'
                          : 'follow up on our session...'
                      }`}
                      className="text-neutral-150"
                      containerClassName="w-full mb-8"
                      initialContentMarkdown={value}
                      onChange={(editorState) => {
                        editorState.read(() => {
                          onChange(
                            $convertToMarkdownString(
                              MARKDOWN_TRANSFORMERS,
                              undefined,
                              true,
                            ),
                          );
                        });
                      }}
                      onError={(error: Error) => {
                        console.error(error);
                      }}
                    />
                  );
                }}
              />
              <SectionLabel title="Attach Activities and Resources" />
              <div className="mb-6 mt-4 flex w-full flex-row items-center justify-between">
                <PillButton
                  IconComponent={BookOpenIcon}
                  containerClassName="mx-2"
                  title="Library"
                  onClick={() => {
                    setIsAddResourceModalOpen(true);
                  }}
                />
                <PillButton
                  IconComponent={ClipboardCheckIcon}
                  containerClassName="mx-2"
                  title="Assessments"
                  onClick={() => {
                    setIsAddStandardAssessmentsModalOpen(true);
                  }}
                />
                <PillButton
                  IconComponent={CircleCheck}
                  containerClassName="mx-2"
                  title="Activity"
                  onClick={addActionActivity}
                />
                <AddANewResourceMenu
                  onClickAddLink={() => setIsAddLinkModalOpen(true)}
                  onClickAddImage={() => setIsAddImageModalOpen(true)}
                  onClickAddPDF={() => setIsAddPDFModalOpen(true)}
                  onClickAddAudio={() => setIsAddAudioModalOpen(true)}
                  onClickAddText={() => setIsAddOrEditTextModalOpen(true)}
                />
              </div>

              {/* Activities list */}
              {activities.map((activity, activityIndex) => {
                return (
                  <ActivityCreationCard
                    activity={activity}
                    {...activity.activityPreviewCardProps}
                    key={activity.id}
                    activityIndex={activityIndex}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    errors={errors}
                    activitiesLength={activities.length}
                    onClickCloseX={() => {
                      remove(activityIndex);
                    }}
                    onClickModifyFileSettings={(activityPdfId: string) => {
                      setSelectedActivityPdfId(activityPdfId);
                      setIsFileSettingsModalOpen(true);
                    }}
                    handleUpdateActivityUseType={handleUpdateActivityUseType}
                    requiresSendingProviderSignatureFirst={requiresProviderSignatureFirst(
                      activity,
                    )}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </PageContainer>
      <AddLinkModal
        isModalOpen={isAddLinkModalOpen}
        setClosed={() => setIsAddLinkModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddPDFModal
        isModalOpen={isAddPDFModalOpen}
        setClosed={() => setIsAddPDFModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddImageModal
        isModalOpen={isAddImageModalOpen}
        setClosed={() => setIsAddImageModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddAudioModal
        isModalOpen={isAddAudioModalOpen}
        setClosed={() => setIsAddAudioModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddOrEditTextModal
        isModalOpen={isAddOrEditTextModalOpen}
        setClosed={() => setIsAddOrEditTextModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddResourceModal
        isModalOpen={isAddResourceModalOpen}
        setClosed={() => setIsAddResourceModalOpen(false)}
        prependToFollowUp={prepend}
        ref={addResourceModalRef}
      />
      <AddStandardAssessmentsModal
        isModalOpen={isAddStandardAssessmentsModalOpen}
        setClosed={() => setIsAddStandardAssessmentsModalOpen(false)}
        prependToFollowUp={prepend}
      />
      <FileSettingsModal
        followUpMode
        isModalOpen={isFileSettingsModalOpen}
        setClosed={() => setIsFileSettingsModalOpen(false)}
        activityPdfId={selectedActivityPdfId}
        onUpdated={(updatedActivityPdf: ActivityPdf) => {
          // Replace the form activity in the list with the updated provider activity from the server
          const updatedActivityIndex = activities.findIndex(
            (activity) =>
              activity.sendFollowUpActivityInputRaw.activityPDFId ===
              updatedActivityPdf.id,
          );
          if (updatedActivityIndex !== -1) {
            update(
              updatedActivityIndex,
              formatProviderActivityForFollowUp(updatedActivityPdf),
            );
          }
          // Library internal to the AddResourceModal must be refreshed since we've made a change to the file
          addResourceModalRef.current?.refetchProviderActivities();
        }}
      />
      <ReviewSignerDetailsModal
        setClosed={() => {
          setIsReviewSignerDetailsModalOpen(false);
        }}
        isOpen={isReviewSignerDetailsModalOpen}
        sendingProviderUser={authedProviderUser}
        multiSignatureActivities={unassignedMultiSignatureActivities}
        patient={undefined}
        patients={
          patientsDataForModal.length > 0 ? patientsDataForModal : undefined
        }
        update={update}
        refetchPatient={refetchPatient}
        refetchPatientForModal={refetchPatientForModal}
        sendFollowUpWithCheck={sendFollowUpWithCheck}
        submitting={submitting}
      />
      <ConfirmDeleteModal
        title="Would you like to delete this mail draft?"
        actionButtonTitle="Delete mail draft"
        isOpen={isDeleteFollowUpDraftModalOpen}
        fetching={submitting}
        setClosed={() => setIsDeleteFollowUpDraftModalOpen(false)}
        performDelete={async () => void 0}
      />
      <FollowUpIntroModal
        isModalOpen={isFollowUpIntroModalOpen}
        setClosed={() => setIsFollowUpIntroModalOpen(false)}
        todayFormatted={todayFormatted}
      />
      <SendToTagConfirmationModal
        isModalOpen={isSendToTagConfirmationModalOpen}
        setClosed={() => setIsSendToTagConfirmationModalOpen(false)}
        onConfirm={() => {
          setIsSendToTagConfirmationModalOpen(false);
          sendFollowUpWithCheck(true);
        }}
        recipientPatientNames={recipientPatientNames}
      />
      <NameFollowUpTemplateModal
        isOpen={isNameFollowUpTemplateModalOpen}
        setClosed={() => setIsNameFollowUpTemplateModalOpen(false)}
        followUpTemplateName={watchSubject}
        handleSaveFollowUpTemplate={handleSaveFollowUpTemplate}
      />
      <NameFollowUpTemplateModal
        isOpen={isRenameFollowUpTemplateModalOpen}
        setClosed={() => setIsRenameFollowUpTemplateModalOpen(false)}
        followUpTemplateName={followUpTemplateToRename?.name}
        handleRenameFollowUpTemplate={handleRenameFollowUpTemplate}
        existingFollowUpTemplate={followUpTemplateToRename}
        handleSaveFollowUpTemplate={handleSaveFollowUpTemplate}
      />
      {templateInUseInfo && (
        <ResourceInUseModal
          isOpen={!!templateInUseInfo}
          setClosed={() => setTemplateInUseInfo(null)}
          resourceType="email_template"
          resourceName={templateInUseInfo.templateName}
          automationReferences={templateInUseInfo.automationReferences}
          canViewAutomations={isProviderUserOwnerOrAdminAuthorized(
            authedProviderUser,
          )}
        />
      )}
      {Boolean(!showPage || saveFollowUpTemplateMutationLoading) && (
        <Spinner className="mx-auto mt-24" />
      )}
    </>
  );
};

export default FollowUpBuilder;
